import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Icon, Input } from 'antd'
import OrderInfoPanel from './OrderInfoPanel'
import MediaViewer from '../../shared/camera/MediaViewer'
import {
    MAX_IMAGES, MAX_VIDEOS, isVideo, countRowMedia, isPhotoRequested, getPhotoStatus, orderNumberOf,
} from './mediaConstraints'

// 1 dòng = 1 đơn hàng. Hiển thị thông tin đơn, nút Scan (mở camera quét + chụp/quay),
// lưới thumbnail (ảnh ĐÃ LƯU trên server + ảnh/video CHỜ upload), và nút chọn file (fallback).
// Giới hạn áp PER-ĐƠN. Chụp/quay không upload ngay — chỉ thêm vào danh sách chờ của dòng.
// Validate tập trung ở container (_addFilesToRow) — file này chỉ chuyển file thô lên.

// Ảnh server vừa upload đôi khi CHƯA sẵn sàng ngay (GET ngay sau PUT, lại khác backend) → load
// lỗi. Component này tự thử lại vài lần (cache-bust) để ảnh hiện khi server kịp xử lý.
const ThumbImage = ({ src, className, onClick }) => {
    const [displaySrc, setDisplaySrc] = useState(src)
    const triesRef = useRef(0)
    const timerRef = useRef(null)
    useEffect(() => {
        triesRef.current = 0
        setDisplaySrc(src)
        return () => { if (timerRef.current) clearTimeout(timerRef.current) }
    }, [src])
    const _onError = () => {
        if (triesRef.current >= 4) return
        const next = triesRef.current + 1
        timerRef.current = setTimeout(() => {
            triesRef.current = next
            const sep = src.indexOf('?') === -1 ? '?' : '&'
            setDisplaySrc(`${src}${sep}r=${next}-${Date.now()}`)
        }, 1000 * next) // 1s, 2s, 3s, 4s
    }
    return <img src={displaySrc} className={className} onClick={onClick} onError={_onError} alt="" />
}

ThumbImage.propTypes = {
    src: PropTypes.string,
    className: PropTypes.string,
    onClick: PropTypes.func,
}

const PhotoRow = ({
    row, index, hasCamera, cameraBlocked, disabled, autoFocusBarcode,
    onOpenScan, onOpenCapture, onSubmitBarcode, onAddFiles, onRemovePending, onRemoveExisting, onRemoveRow,
}) => {
    const inputRef = useRef(null)
    const barcodeRef = useRef(null)
    const [previewIdx, setPreviewIdx] = useState(-1) // index ảnh/video đang xem full (-1 = đóng)

    const { id, orderData, barcode, existingPhotos, pendingFiles, error, loading } = row
    const identified = !!row.requestId

    // Danh sách media xem full: ảnh đã lưu trước, rồi tới ảnh/video đang chờ (khớp thứ tự thumbnail).
    const viewerItems = [
        ...existingPhotos.map(p => ({ url: p.url, video: isVideo(p.mimetype) })),
        ...pendingFiles.map(f => ({ url: f.preview, video: isVideo(f.file.type) })),
    ]

    // Ô nhập tay mã đơn. Đồng bộ khi barcode thay đổi từ ngoài (ví dụ quét camera điền vào).
    const [barcodeInput, setBarcodeInput] = useState(barcode || '')
    useEffect(() => { setBarcodeInput(barcode || '') }, [barcode])
    const _submitBarcode = () => onSubmitBarcode(id, barcodeInput)

    // PC: tự focus ô nhập khi dòng vừa được thêm (container set autoFocusBarcode).
    useEffect(() => {
        if (autoFocusBarcode && barcodeRef.current && barcodeRef.current.focus) {
            barcodeRef.current.focus()
        }
    }, [autoFocusBarcode])

    // Chỉ cho chụp/upload khi đơn ở trạng thái cần chụp (photo_requested).
    const photoAllowed = !identified || isPhotoRequested(orderData)
    const blockedByStatus = identified && !photoAllowed

    const { images, videos } = countRowMedia(existingPhotos, pendingFiles)
    const isFull = images >= MAX_IMAGES && videos >= MAX_VIDEOS
    const disableAdd = disabled || isFull || blockedByStatus

    const _handleNativeSelectedFiles = (fileList) => {
        if (fileList && fileList.length) onAddFiles(id, Array.from(fileList)) // container validate
        if (inputRef.current) inputRef.current.value = ''
    }

    const _openFilePicker = () => {
        if (disableAdd) return
        if (inputRef.current) inputRef.current.click()
    }

    // Bấm ô [+]: ưu tiên camera in-app (không qua thư viện máy) ở chế độ CHỤP — chưa có đơn thì
    // ảnh barcode đầu tiên sẽ tự nhận diện đơn. PC không camera → fallback chọn file.
    const _onPlaceholderClick = () => {
        if (disableAdd) return
        if (hasCamera) onOpenCapture(id)
        else _openFilePicker()
    }

    const _renderThumb = (key, mimetype, url, onRemove, locked, viewIndex) => {
        const open = () => setPreviewIdx(viewIndex)
        return (
            <div className={`thumb ${locked ? 'thumb-locked' : ''}`} key={key}>
                {isVideo(mimetype)
                    ? <video src={url} muted className="cursor-pointer" onClick={open} />
                    : <ThumbImage src={url} className="cursor-pointer" onClick={open} />}
                {isVideo(mimetype)
                    ? <span className="thumb-play" aria-hidden="true"><Icon type="play-circle" /></span>
                    : <span className="thumb-preview" aria-hidden="true"><Icon type="eye" /></span>}
                {/* Ảnh mã đơn (locked) → badge, không xóa. Đơn KHÔNG ở trạng thái cho phép
                    (blockedByStatus) → chỉ-xem, KHÔNG cho xóa (upload/lưu 1 lần, không sửa). */}
                {locked ? (
                    <span className="thumb-badge" title="Ảnh mã đơn — không thể xóa">Mã đơn</span>
                ) : blockedByStatus ? null : (
                    <button type="button" className="thumb-remove" onClick={onRemove} aria-label="Xóa">
                        <Icon type="close" />
                    </button>
                )}
            </div>
        )
    }

    // Vừa upload thành công trong phiên này (khác với đơn vốn đã ở trạng thái khác từ trước).
    const uploaded = !!row.justUploaded
    const rowClass = ['PhotoRow']
    if (error) rowClass.push('has-error')
    else if (blockedByStatus && !uploaded) rowClass.push('is-blocked')
    else if (identified) rowClass.push('is-identified')

    return (
        <div className={rowClass.join(' ')}>
            <div className="row-header">
                <span className="row-index">#{index + 1}</span>

                {identified ? (
                    <span className="row-title">
                        <span className="row-code">#{orderNumberOf(barcode)}</span>
                    </span>
                ) : (
                    // Chưa có đơn → nhập mã đơn HOẶC chạm nút scan (icon) để mở camera quét.
                    // Nhập tay là fallback cho PC không camera / máy scan vật lý.
                    <div className="row-entry">
                        {/* Bọc input trong form RIÊNG: trên mobile có nhiều input, nút bàn phím sẽ là
                            "Go/Search" và submit form này (thay vì "Next" nhảy sang input dòng khác). */}
                        <form className="row-barcode-form" onSubmit={(e) => { e.preventDefault(); _submitBarcode() }}>
                            <Input
                                ref={barcodeRef}
                                className="row-barcode-input"
                                type="search"
                                size="large"
                                placeholder={hasCamera ? 'Nhập mã đơn…' : 'Nhập mã đơn rồi Enter, ví dụ #123456_1'}
                                prefix={<Icon type={loading ? 'loading' : 'search'} />}
                                value={barcodeInput}
                                disabled={disabled || loading}
                                onChange={(e) => setBarcodeInput(e.target.value)}
                                onPressEnter={_submitBarcode}
                            />
                        </form>
                        {hasCamera && (
                            <Button
                                type="primary"
                                icon="scan"
                                disabled={disabled}
                                onClick={() => onOpenScan(id)}
                                className="row-scan-btn"
                                aria-label="Scan mã đơn bằng camera"
                            />
                        )}
                    </div>
                )}

                {onRemoveRow && (
                    <Button
                        type="link"
                        className="row-remove"
                        icon="delete"
                        disabled={disabled}
                        onClick={() => onRemoveRow(id)}
                        aria-label="Xóa đơn"
                    />
                )}
            </div>

            {blockedByStatus && (
                uploaded ? (
                    // Vừa upload ảnh thành công trong phiên này → thông báo tích cực.
                    <div className="alert alert-success mt-2 mb-0">
                        <Icon type="check-circle" /> Đã tải ảnh lên — đơn ở trạng thái <strong>{getPhotoStatus(orderData)}</strong>.
                    </div>
                ) : (
                    // Chụp/tải đơn KHÔNG ở trạng thái photo_requested (đã updated/confirmed từ trước, hoặc khác).
                    <div className="alert alert-warning mt-2 mb-0">
                        Đơn ở trạng thái <strong>{getPhotoStatus(orderData) || 'khác'}</strong>.
                    </div>
                )
            )}

            {error && <div className="alert alert-danger mt-2 mb-0">{error}</div>}

            {/* Camera bị chặn/từ chối (toast biến mất nhanh) → hint CỐ ĐỊNH chỉ đường fallback:
                nhập mã đơn vào ô rồi Enter để xác định đơn (không cần camera). */}
            {cameraBlocked && !identified && (
                <div className="alert alert-warning mt-2 mb-0">
                    Không dùng được camera để quét. Hãy <strong>nhập mã đơn</strong> vào ô ở trên rồi
                    Enter để xác định đơn.
                </div>
            )}

            {/* Chỉ hiện thông tin đơn + khu vực ảnh/video SAU KHI đã scan nhận diện đơn.
                PC: 2 cột — list ảnh (trái) + thông tin đơn (phải). Mobile: xếp dọc (thông tin trên). */}
            {identified && (
            <div className="row-body mt-2">
                <OrderInfoPanel orderData={orderData} barcode={barcode} />

                <div className="row-media">
                    {(identified || pendingFiles.length > 0) && (
                        <div className="media-summary">
                            <span><Icon type="picture" /> {images} ảnh</span>
                            <span><Icon type="video-camera" /> {videos} video</span>
                            {pendingFiles.length > 0 && (
                                <span className="pending-count">
                                    <Icon type="clock-circle" /> {pendingFiles.length} chờ lưu
                                </span>
                            )}
                        </div>
                    )}

                    <div className="thumb-grid thumb-grid--scroll py-3 px-3 border rounded mt-2">
                        {existingPhotos.map((p, i) =>
                            _renderThumb(`old-${id}-${i}`, p.mimetype, p.url, () => onRemoveExisting(id, i), false, i))}
                        {pendingFiles.map((f, i) =>
                            _renderThumb(`new-${id}-${i}`, f.file.type, f.preview, () => onRemovePending(id, i), f.isBarcode, existingPhotos.length + i))}
                        <div
                            className={`thumb thumb-placeholder ${disableAdd ? 'is-disabled' : 'cursor-pointer'}`}
                            onClick={_onPlaceholderClick}
                        >
                            <Icon type={hasCamera ? 'camera' : 'picture'} theme="filled" />
                            <span className="plus-icon">+</span>
                        </div>
                    </div>

                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        accept="image/*,video/*"
                        disabled={disabled}
                        style={{ display: 'none' }}
                        onChange={(e) => _handleNativeSelectedFiles(e.target.files)}
                    />

                    <div className="row-footer mt-2">
                        {hasCamera && (
                            <Button
                                type="primary"
                                shape="circle"
                                icon="camera"
                                disabled={disableAdd}
                                onClick={() => onOpenCapture(id)}
                                className="mr-2"
                                aria-label="Chụp ảnh / Quay video"
                            />
                        )}
                        <Button type="default" size="small" icon="upload" disabled={disableAdd} onClick={_openFilePicker}>
                            Chọn file để tải lên
                        </Button>
                    </div>
                </div>
            </div>
            )}

            {previewIdx >= 0 && viewerItems[previewIdx] && (
                <MediaViewer
                    items={viewerItems}
                    index={previewIdx}
                    onIndexChange={setPreviewIdx}
                    onClose={() => setPreviewIdx(-1)}
                />
            )}
        </div>
    )
}

PhotoRow.propTypes = {
    row: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    hasCamera: PropTypes.bool,
    cameraBlocked: PropTypes.bool,
    disabled: PropTypes.bool,
    autoFocusBarcode: PropTypes.bool,
    onOpenScan: PropTypes.func.isRequired,
    onOpenCapture: PropTypes.func.isRequired,
    onSubmitBarcode: PropTypes.func.isRequired,
    onAddFiles: PropTypes.func.isRequired,
    onRemovePending: PropTypes.func.isRequired,
    onRemoveExisting: PropTypes.func.isRequired,
    onRemoveRow: PropTypes.func,
}

export default PhotoRow
